import { Promise } from 'bluebird';
import 'isomorphic-fetch';
import { v4 as uuidv4 } from 'uuid';
import { CORRELATION_ID_HEADER } from '../constants';

function getCorrelationIdFromHeader(response) {
    return response.headers.get(CORRELATION_ID_HEADER);
}

function safeStringify(obj) {
    // Prototype.js pollutes arrays on legacy pages which causes them to be
    // double stringified. Carefully remove the prototype method then put it
    // back to prevent unwanted side effects in the rest of the application.
    const arrayToJson = Array.prototype.toJSON;

    if (arrayToJson) {
        delete Array.prototype.toJSON;
    }

    try {
        return JSON.stringify(obj);
    } finally {
        if (arrayToJson) {
            // eslint-disable-next-line no-extend-native
            Array.prototype.toJSON = arrayToJson;
        }
    }
}

export function fetchFile(url) {
    return Promise.try(() => {
        const fetchOptions = {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        };

        return fetch(url, fetchOptions);
    })
        .then((response) => response.json().then((responseBody) => ({
            response,
            responseBody,
        })))
        .then(({ response, responseBody }) => {
            const { status } = response;

            if (status >= 400) {
                throw new Error(`fetch call failed with status code ${status}`);
            }

            return {
                data: responseBody.data,
            };
        });
}

export default function fetchJson(apiContext, query, correlationId) {
    return Promise.try(() => {
        const {
            jwt,
            url,
        } = apiContext;

        const concurCorrelationId = correlationId || apiContext.correlationId || uuidv4();
        const fetchOptions = {
            credentials: 'include',
            method: 'POST',
            body: safeStringify(query),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                [CORRELATION_ID_HEADER]: concurCorrelationId,
                ...(typeof jwt === 'string' && { Authorization: `Bearer ${jwt}` }),
            },
        };

        // If the API context provides its own fetch function, use it
        // That allows fetchOverXhr to be provided and used when needed
        const fetchFunction = typeof apiContext.fetch === 'function' ? apiContext.fetch : fetch;

        return fetchFunction(url, fetchOptions);
    })
        .then((response) => response.json().then((responseBody) => ({
            response,
            responseBody,
        })))
        .then(({ response, responseBody }) => {
            const { status } = response;
            const correlationIdFromResponse = getCorrelationIdFromHeader(response);
            const error = responseBody.errors ? responseBody.errors?.[0] : responseBody.error;

            if (error || status >= 400) {
                let errorMessage = `CDS call failed with status code ${status}:`;
                const { message, errorCode } = error || {};
                if (errorCode) {
                    errorMessage += `, error code: ${errorCode}`;
                }
                if (correlationIdFromResponse) {
                    errorMessage += `, [correlationId: ${correlationIdFromResponse}]`;
                }
                if (message) {
                    errorMessage += ` ${message}`;
                }
                throw new Error(errorMessage);
            }

            return {
                data: responseBody.data,
                meta: {
                    correlationId: correlationIdFromResponse,
                },
            };
        });
}
